import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import type { SellerResponseDTO } from "@/features/sellers/types/seller.types";
import { step1Schema, type Step1Data, type NewClientData } from "../../schemas";
import {
  ContractSelector,
  type ContractOption,
  type ClientOption,
} from "./ContractSelector";

interface WizardStep1Props {
  defaultValues?: Partial<Step1Data>;
  sellers: SellerResponseDTO[];
  searchContracts: (
    term: string,
    signal?: AbortSignal,
  ) => Promise<ContractOption[]>;
  createContract: (
    contractNumber: string,
    clientId: number,
    signal?: AbortSignal,
  ) => Promise<{ id: number; contractNumber: string }>;
  searchClients: (
    term: string,
    signal?: AbortSignal,
  ) => Promise<ClientOption[]>;
  createClient: (
    data: NewClientData,
    signal?: AbortSignal,
  ) => Promise<{ id: number; name: string }>;
  onNext: (data: Step1Data) => void;
}

export const WizardStep1 = ({
  defaultValues,
  sellers,
  searchContracts,
  createContract,
  searchClients,
  createClient,
  onNext,
}: WizardStep1Props) => {
  const form = useForm<Step1Data>({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      contractId: defaultValues?.contractId ?? 0,
      contractDisplay: defaultValues?.contractDisplay ?? "",
      sellerId: defaultValues?.sellerId ?? 0,
    },
  });

  const contractId = form.watch("contractId");
  const contractDisplay = form.watch("contractDisplay");

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onNext)} className="space-y-5">
        <FormField
          control={form.control}
          name="contractId"
          render={() => (
            <FormItem>
              <FormLabel>Contrato *</FormLabel>
              <FormControl>
                <ContractSelector
                  selectedId={contractId || null}
                  selectedDisplay={contractDisplay}
                  onSelect={(id, display) => {
                    form.setValue("contractId", id, { shouldValidate: true });
                    form.setValue("contractDisplay", display);
                  }}
                  onClear={() => {
                    form.setValue("contractId", 0, { shouldValidate: true });
                    form.setValue("contractDisplay", "");
                  }}
                  searchContracts={searchContracts}
                  createContract={createContract}
                  searchClients={searchClients}
                  createClient={createClient}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="sellerId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Vendedor *</FormLabel>
              <FormControl>
                <select
                  value={field.value || ""}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Selecione um vendedor</option>
                  {sellers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end pt-2">
          <Button type="submit">Próximo</Button>
        </div>
      </form>
    </Form>
  );
};
